import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Mission } from "@/services/clientDataService";
import {
  calculatePaymentModalityDefaults,
  calculateMinimumMonthlyCost,
  PaymentModalityData as CalculationPaymentModalityData,
} from "@/utils/synthesisCalculations";
import {
  calculateSliderDuration,
  calculateSliderMonthlyPayment,
  calculateMonthlyCostForMode,
  generateDeveloperPaliers,
  SLIDER_CONSTANTS,
  type PricingMode as SliderPricingMode,
} from "@/utils/paymentModalityCalculations";
import type { PackAilixConfig } from "@/utils/packAilixPricing";
import { isHybridActiveForScope } from "@/utils/packAilixPricing";
import {
  savePaymentModality,
  getPaymentModality,
  PaymentModalityData,
  CurrentMissionsData,
} from "@/services/devisService";
import { formatEuroTruncated } from "@/lib/formatUtils";

type PricingMode = "a_la_carte" | "pack_ailix_1an" | "pack_ailix_2ans";

export interface PaymentModalityState {
  /** Mensualité réelle (totalCost / durée arrondie) — conservée pour la sauvegarde */
  monthlyCost: number;
  /** Mensualité de comparaison (base 18j/dev/mois + prestations du mode) */
  comparisonMonthlyCost: number;
  developers: number;
  projectMonths: number;
  exactMonths?: number;
  isCustom: boolean;
  isSaving: boolean;
  lastSaved?: Date;
}

interface UsePaymentModalityStateParams {
  selectedMissions: Mission[];
  totalCost: number;
  clientId: string;
  pricingMode: PricingMode;
  packConfig?: PackAilixConfig | null;
  clientTjm?: number;
  clientTjmMoe?: number;
  toast: (config: {
    title: string;
    description?: string;
    variant?: "default" | "destructive";
  }) => void;
}

interface UsePaymentModalityStateResult {
  shouldRender: boolean;
  state: PaymentModalityState;
  defaultData: CalculationPaymentModalityData;
  sliderStep: number;
  currentMinimumCost: number;
  handleDevelopersChange: (value: number[]) => void;
  handleSave: () => Promise<void>;
  handleRecommended: () => void;
}

/**
 * Calcule les jours totaux de façon normalisée (comme le slider),
 * en parsant duration si duration_days est absent.
 */
function computeNormalizedTotalDays(missions: Mission[]): number {
  if (!Array.isArray(missions)) return 0;
  return missions.reduce((sum, mission) => {
    if (!mission) return sum;
    const days = mission.duration_days
      || parseFloat(((mission.duration?.split(" ")[0] || "0").replace(",", ".")))
      || 0;
    return sum + (isNaN(days) ? 0 : days);
  }, 0);
}

function buildCurrentMissionsData(
  selectedMissions: Mission[],
  totalCost: number
): CurrentMissionsData {
  if (!Array.isArray(selectedMissions)) {
    return {
      total_cost: totalCost || 0,
      total_days: 0,
      missions_count: 0,
      selected_missions: [],
      missions_details: [],
      mission_titles: [],
    };
  }

  const totalDays = computeNormalizedTotalDays(selectedMissions);

  return {
    total_cost: totalCost || 0,
    total_days: totalDays,
    missions_count: selectedMissions.length,
    selected_missions: selectedMissions.map((mission) => ({
      id: mission?.id || "unknown",
      type: "mission",
      quantity: 1,
    })),
    missions_details: selectedMissions.map((mission) => ({
      id: mission?.id || "unknown",
      title: mission?.title || "Mission inconnue",
      duration: mission?.duration_days ? `${mission.duration_days} jours` : mission?.duration || "0 jours",
      price: mission?.price || 0,
      description: mission?.description || "",
      category: mission?.category || "Autre",
    })),
    mission_titles: selectedMissions.map((mission) => mission?.title || "Mission inconnue"),
  };
}

/**
 * Calcule un scopeKey stable pour détecter les changements de sélection.
 * Inclut les IDs triés, le coût total et les jours totaux.
 */
function computeScopeKey(missions: Mission[], totalCost: number): string {
  if (!Array.isArray(missions) || missions.length === 0) return "";
  const ids = missions.map((m) => m?.id || "").filter(Boolean).sort().join(",");
  const days = computeNormalizedTotalDays(missions);
  return `${ids}|${totalCost}|${days}`;
}

export function usePaymentModalityState({
  selectedMissions,
  totalCost,
  clientId,
  pricingMode,
  packConfig,
  clientTjm,
  clientTjmMoe,
  toast,
}: UsePaymentModalityStateParams): UsePaymentModalityStateResult {
  const previousScopeKeyRef = useRef<string>("");
  // F1 (DEVIS-MODALITY-LOOP-001) : mémorise le dernier (clientId:scopeKey) chargé pour ne fetch
  // qu'une fois par scope réel et casser la boucle de rendu → flood → ban IP.
  const loadedScopeKeyRef = useRef<string>("");

  const shouldRender = Array.isArray(selectedMissions) && selectedMissions.length > 0 && totalCost > 0;

  // Config effective : forcer legacy si l'hybride n'est pas actif pour le scope
  // Préserve les TJM custom tout en désactivant les prestations hybrides
  const totalDaysForHybrid = computeNormalizedTotalDays(selectedMissions);
  const hybridActive = isHybridActiveForScope(packConfig, totalDaysForHybrid);
  const effectiveConfig = useMemo(
    () => hybridActive
      ? packConfig
      : packConfig ? { ...packConfig, comparison_model: 'legacy' as const } : packConfig,
    [hybridActive, packConfig],
  );

  const scopeKey = useMemo(
    () => computeScopeKey(selectedMissions, totalCost),
    [selectedMissions, totalCost]
  );

  const currentMissionsData = useMemo(
    () => buildCurrentMissionsData(selectedMissions, totalCost),
    [selectedMissions, totalCost]
  );

  const defaultData: CalculationPaymentModalityData = useMemo(() => {
    if (!shouldRender) {
      return {
        totalDays: 0,
        defaultDevelopers: 1,
        minDevelopers: 1,
        maxDevelopers: 2,
        defaultMonths: 1,
        defaultMonthlyCost: totalCost || 0,
        minimumMonthlyCost: totalCost || 0,
        maximumMonthlyCost: totalCost || 0,
      };
    }
    return calculatePaymentModalityDefaults(selectedMissions, totalCost);
  }, [selectedMissions, totalCost, shouldRender]);

  /** Calcule l'état par défaut (2 devs) pour le scope courant */
  const computeDefaultState = useCallback((): Omit<PaymentModalityState, "isSaving" | "lastSaved"> => {
    const defaultDevs = SLIDER_CONSTANTS.DEFAULT_DEVS;
    const totalDays = defaultData.totalDays || 0;
    const duration = calculateSliderDuration(totalDays, defaultDevs);
    const monthlyCost = totalCost > 0 ? calculateSliderMonthlyPayment(totalCost, duration) : 0;
    const comparisonMonthlyCost = calculateMonthlyCostForMode(
      defaultDevs,
      pricingMode as SliderPricingMode,
      effectiveConfig,
      undefined,
      clientTjm,
      clientTjmMoe,
    );
    return {
      monthlyCost,
      comparisonMonthlyCost,
      developers: defaultDevs,
      projectMonths: duration,
      exactMonths: duration,
      isCustom: false,
    };
  }, [defaultData.totalDays, totalCost, pricingMode, effectiveConfig, clientTjm, clientTjmMoe]);

  const [state, setState] = useState<PaymentModalityState>(() => {
    const defaults = computeDefaultState();
    return { ...defaults, isSaving: false, lastSaved: undefined };
  });

  const preCalculatedValues = useMemo(() => {
    const values = new Map<
      number,
      {
        monthlyCost: number;
        comparisonMonthlyCost: number;
        developers: number;
        projectMonths: number;
        exactMonths: number;
        isCustom: boolean;
      }
    >();

    if (!shouldRender) return values;

    const totalDays = defaultData.totalDays || 0;
    const paliers = generateDeveloperPaliers();

    for (const nbDevs of paliers) {
      const duration = calculateSliderDuration(totalDays, nbDevs);
      const monthlyCost = calculateSliderMonthlyPayment(totalCost, duration);
      const comparisonMonthlyCost = calculateMonthlyCostForMode(
        nbDevs,
        pricingMode as SliderPricingMode,
        effectiveConfig,
        undefined,
        clientTjm,
        clientTjmMoe,
      );

      values.set(nbDevs, {
        monthlyCost,
        comparisonMonthlyCost,
        developers: nbDevs,
        projectMonths: duration,
        exactMonths: duration,
        isCustom: nbDevs !== SLIDER_CONSTANTS.DEFAULT_DEVS,
      });
    }

    return values;
  }, [defaultData.totalDays, totalCost, pricingMode, effectiveConfig, clientTjm, clientTjmMoe, shouldRender]);

  // Charger la modalité sauvegardée, uniquement si match exact ou legacy compatible.
  // F1 (DEVIS-MODALITY-LOOP-001) : le fetch est gardé par le scope métier STABLE (clientId + scopeKey)
  // via loadedScopeKeyRef, et NON par les objets dérivés (currentMissionsData / effectiveConfig) qui
  // changent de référence à chaque render → ancienne boucle de rendu infinie → flood réseau → ban IP.
  useEffect(() => {
    let cancelled = false;

    const loadSavedModality = async () => {
      if (!shouldRender || !clientId || !scopeKey) return;

      // scopeKey (computeScopeKey) = ids + coût + jours, SANS clientId → clé composite obligatoire,
      // sinon changer de client à scope identique skipperait à tort le fetch légitime.
      const loadKey = `${clientId}:${scopeKey}`;
      if (loadedScopeKeyRef.current === loadKey) return;
      // Poser le ref AVANT l'await : bloque la ré-entrée synchrone tant qu'un fetch est en vol
      // (sinon un re-render rapide relancerait getPaymentModality avant la résolution du précédent).
      loadedScopeKeyRef.current = loadKey;

      try {
        const result = await getPaymentModality(clientId, currentMissionsData);
        if (cancelled) return;

        const matchType = result.matchType || "default";

        // N'appliquer la modalité sauvegardée que si match exact ou legacy_fallback
        if (result.success && result.data && (matchType === "exact" || matchType === "legacy_fallback")) {
          const savedData = result.data;
          const roundedSavedMonths = Math.max(2, Math.round(savedData.project_months * 2) / 2);

          // Sanity check : la durée sauvegardée est-elle cohérente avec les jours courants ?
          const currentTotalDays = defaultData.totalDays || 0;
          if (currentTotalDays > 0 && savedData.developer_count > 0) {
            const expectedDuration = calculateSliderDuration(currentTotalDays, savedData.developer_count);
            const durationDrift = Math.abs(roundedSavedMonths - expectedDuration);
            if (durationDrift > 2) {
              return;
            }
          }

          let exactSavedMonthlyCost = savedData.monthly_cost;
          if (totalCost > 0 && roundedSavedMonths !== savedData.project_months) {
            exactSavedMonthlyCost = Math.round(totalCost / roundedSavedMonths);
          }

          const clampedDevs = Math.min(
            Math.max(savedData.developer_count, SLIDER_CONSTANTS.MIN_DEVS),
            SLIDER_CONSTANTS.MAX_DEVS
          );

          const nextIsCustom = savedData.is_custom || clampedDevs !== SLIDER_CONSTANTS.DEFAULT_DEVS;
          const nextLastSaved = savedData.saved_at ? new Date(savedData.saved_at) : undefined;

          // F3 (defense-in-depth) : ne re-render que si une valeur change réellement.
          // NB : comparisonMonthlyCost n'est PAS écrit ici — il appartient exclusivement à l'effect de
          // recompute (déps : mode + state.developers), ce qui évite d'écrire une comparaison calculée
          // avec un pricingMode périmé si le mode a changé pendant le fetch en vol (race round 2).
          setState((prev) => {
            if (
              prev.monthlyCost === exactSavedMonthlyCost &&
              prev.developers === clampedDevs &&
              prev.projectMonths === roundedSavedMonths &&
              prev.exactMonths === roundedSavedMonths &&
              prev.isCustom === nextIsCustom &&
              prev.lastSaved?.getTime() === nextLastSaved?.getTime()
            ) {
              return prev;
            }
            return {
              ...prev,
              monthlyCost: exactSavedMonthlyCost,
              developers: clampedDevs,
              projectMonths: roundedSavedMonths,
              exactMonths: roundedSavedMonths,
              isCustom: nextIsCustom,
              lastSaved: nextLastSaved,
            };
          });
        }
      } catch (error) {
        if (!cancelled) {
          // NE PAS réinitialiser loadedScopeKeyRef ici : en cas d'erreur persistante (403/429),
          // ré-armer relancerait le flood. Le scope sera rechargé naturellement s'il change.
          console.error("usePaymentModalityState: failed loading saved modality", error);
        }
      }
    };

    loadSavedModality();
    return () => { cancelled = true; };
    // Gating intentionnel sur le scope métier stable (clientId + scopeKey) via loadedScopeKeyRef.
    // currentMissionsData/totalCost/defaultData.totalDays sont lus dans le corps mais NE doivent PAS
    // re-déclencher le fetch (sinon on recrée le flood) ; ils dérivent tous de selectedMissions/totalCost,
    // donc du même scopeKey. La comparaison (mode-dépendante) est gérée par l'effect de recompute.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, shouldRender, scopeKey]);

  // Préservation comportement (lié à F1) : recalcule la mensualité de comparaison quand le MODE de
  // pricing OU le nombre de devs change, SANS re-fetch réseau. L'ancien effect de chargement assurait
  // ça via re-fetch (supprimé pour stopper la boucle). `state.developers` est dans les deps pour que
  // la comparaison soit recalculée avec le mode COURANT après que le load effect a appliqué les devs
  // de la modalité sauvegardée (corrige la race « mode changé pendant un fetch en vol »). Bail si
  // inchangé → ne peut pas boucler même si effectiveConfig est une référence instable.
  useEffect(() => {
    if (!shouldRender) return;
    setState((prev) => {
      const next = calculateMonthlyCostForMode(
        prev.developers,
        pricingMode as SliderPricingMode,
        effectiveConfig,
        undefined,
        clientTjm,
        clientTjmMoe,
      );
      return prev.comparisonMonthlyCost === next ? prev : { ...prev, comparisonMonthlyCost: next };
    });
  }, [shouldRender, pricingMode, effectiveConfig, clientTjm, clientTjmMoe, state.developers]);

  // Détection de changement de scope via scopeKey (remplace l'ancien missionsCount)
  useEffect(() => {
    if (!scopeKey || !clientId) {
      previousScopeKeyRef.current = "";
      // Reset aussi le ref de chargement : si l'utilisateur vide puis re-sélectionne le même scope,
      // le GET de la modalité sauvegardée doit pouvoir se relancer (sinon loadKey resterait figé et
      // la modalité ne se ré-appliquerait jamais).
      loadedScopeKeyRef.current = "";
      return;
    }

    // Clé composite IDENTIQUE au fetch (clientId:scopeKey) : sans clientId ici, changer de client à
    // scope identique ne resetterait pas l'état → la modalité du client précédent resterait affichée
    // (et pourrait être sauvegardée sur le mauvais client). Fuite inter-client.
    const resetKey = `${clientId}:${scopeKey}`;
    const prevKey = previousScopeKeyRef.current;
    previousScopeKeyRef.current = resetKey;

    // Premier render OU changement de scope OU changement de client → reset aux valeurs par défaut.
    if (prevKey !== resetKey) {
      const defaults = computeDefaultState();
      setState((prev) => ({
        ...prev,
        ...defaults,
        lastSaved: undefined,
      }));
    }
  }, [clientId, scopeKey, computeDefaultState]);

  const handleDevelopersChange = useCallback(
    (value: number[]) => {
      const requestedDevs = value[0];
      const roundedDevs = Math.round(requestedDevs * 5) / 5;
      const clampedDevs = Math.max(
        SLIDER_CONSTANTS.MIN_DEVS,
        Math.min(SLIDER_CONSTANTS.MAX_DEVS, roundedDevs)
      );

      const precalcValues = preCalculatedValues.get(clampedDevs);
      if (!precalcValues) return;

      setState((prev) => ({
        ...prev,
        monthlyCost: precalcValues.monthlyCost,
        comparisonMonthlyCost: precalcValues.comparisonMonthlyCost,
        developers: precalcValues.developers,
        projectMonths: precalcValues.projectMonths,
        exactMonths: precalcValues.exactMonths,
        isCustom: precalcValues.isCustom,
      }));
    },
    [preCalculatedValues]
  );

  const currentMinimumCost = useMemo(() => {
    const devs =
      typeof state.developers === "number" && isFinite(state.developers)
        ? state.developers
        : defaultData.defaultDevelopers || 1;
    const totalDays =
      typeof defaultData.totalDays === "number" && isFinite(defaultData.totalDays)
        ? defaultData.totalDays
        : 0;
    const projectMonths =
      typeof state.projectMonths === "number" && isFinite(state.projectMonths)
        ? state.projectMonths
        : defaultData.defaultMonths || 1;

    const result = calculateMinimumMonthlyCost(devs, totalDays, projectMonths);
    return isFinite(result) && result > 0 ? result : 0;
  }, [state.developers, state.projectMonths, defaultData]);

  const handleSave = useCallback(async () => {
    if (state.isSaving) return;
    setState((prev) => ({ ...prev, isSaving: true }));

    try {
      const modalityData: PaymentModalityData = {
        monthly_cost: state.monthlyCost,
        developer_count: state.developers,
        project_months: state.projectMonths,
        is_custom: state.isCustom,
        total_days: defaultData.totalDays,
        minimum_monthly_cost: currentMinimumCost,
      };

      const result = await savePaymentModality(clientId, modalityData, currentMissionsData);

      if (result.success) {
        setState((prev) => ({ ...prev, isSaving: false, lastSaved: new Date() }));
        toast({
          title: "Modalité sauvegardée",
          description: `Mensualité de ${formatEuroTruncated(state.monthlyCost)} enregistrée.`,
        });
      } else {
        setState((prev) => ({ ...prev, isSaving: false }));
        toast({
          title: "Erreur de sauvegarde",
          description: result.message || "Impossible d'enregistrer la modalité de paiement.",
          variant: "destructive",
        });
      }
    } catch (error) {
      setState((prev) => ({ ...prev, isSaving: false }));
      toast({
        title: "Erreur de sauvegarde",
        description: "Impossible d'enregistrer la modalité de paiement.",
        variant: "destructive",
      });
    }
  }, [
    clientId,
    currentMinimumCost,
    currentMissionsData,
    defaultData.totalDays,
    state.developers,
    state.isCustom,
    state.isSaving,
    state.monthlyCost,
    state.projectMonths,
    toast,
  ]);

  const handleRecommended = useCallback(() => {
    const recommendedDevs = SLIDER_CONSTANTS.DEFAULT_DEVS;
    const precalcValues = preCalculatedValues.get(recommendedDevs);
    if (!precalcValues) return;

    setState((prev) => ({
      ...prev,
      monthlyCost: precalcValues.monthlyCost,
      comparisonMonthlyCost: precalcValues.comparisonMonthlyCost,
      developers: precalcValues.developers,
      projectMonths: precalcValues.projectMonths,
      exactMonths: precalcValues.exactMonths,
      isCustom: false,
    }));
  }, [preCalculatedValues]);

  return {
    shouldRender,
    state,
    defaultData,
    sliderStep: SLIDER_CONSTANTS.STEP_DEVS,
    currentMinimumCost,
    handleDevelopersChange,
    handleSave,
    handleRecommended,
  };
}
